// AdminLayoutBuilder.js - Updated responsive version

import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Rnd } from 'react-rnd';
import Navbar from './Navbar';
import Footer from './Footer';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUpload, 
  faSave, faMapMarkerAlt,
  faParking,
  faRestroom,
  faUtensils,
  faMedkit,
  faFlag,
  faStar,
  faTrash,
  faCopy,
  faMousePointer,
  faUndo,
  faRedo,
  faPlus,
  faMinus,
  faCheckCircle,
  faExpand,
  faCompress,
  faRotateRight,
  faGrid,
  faRuler,
  faPalette,
  faEye,
  faEyeSlash,
  faArrowsAlt,
  faArrowLeft,
  faChair,
  faCouch,
  faUsers,
  faMusic,
  faShoppingBag,
  faInfoCircle,
  faArrowsAltH,
  faTicketAlt,
  faBan,
  faBars,
  faTimes,
  faChevronLeft,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import './AdminLayoutBuilder.css';

// API Base URL
const API_BASE_URL = 'https://royaleheritagefiesta.com/royaleheritagefiesta/api';
//const API_BASE_URL = 'http://localhost/royaleheritagefiesta/api';

function AdminLayoutBuilder() {
  const location = useLocation();
  const navigate = useNavigate();
  const { fromAddEvent, eventName, eventId, isEditMode, duplicateFrom } = location.state || {};

  const fileInputRef = useRef(null);
  const canvasContainerRef = useRef(null);
  
  const [pendingFormData, setPendingFormData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [backgroundImageUrl, setBackgroundImageUrl] = useState(null);
  const [imageDimensions, setImageDimensions] = useState({ width: 800, height: 1100 });
  const [originalImageDimensions, setOriginalImageDimensions] = useState({ width: 0, height: 0 });
  const [imageOpacity, setImageOpacity] = useState(0.4);
  const [showBlueprint, setShowBlueprint] = useState(true);
  const [imageFitMode, setImageFitMode] = useState('contain');
  
  const [stalls, setStalls] = useState([]);
  const [fixedElements, setFixedElements] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedTool, setSelectedTool] = useState('select');
  const [showGrid, setShowGrid] = useState(true);
  const [gridSize, setGridSize] = useState(40);
  const [snapToGrid, setSnapToGrid] = useState(true);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  
  const [bulkRows, setBulkRows] = useState(3);
  const [bulkCols, setBulkCols] = useState(3);
  const [bulkSpacing, setBulkSpacing] = useState(10);

  const [stallSize, setStallSize] = useState({ width: 80, height: 80 });
  const [stallPrice, setStallPrice] = useState(8000);
  const [stallCategory, setStallCategory] = useState('general');

  const [quickPlaceMode, setQuickPlaceMode] = useState(false);
  const [placementPreview, setPlacementPreview] = useState({ x: 0, y: 0, visible: false });

  // NEW: Ticket Type - 3 options: 'seated', 'standing', 'none'
  const [ticketType, setTicketType] = useState('seated');
  const [hasSeating, setHasSeating] = useState(true);
  const [seatingType, setSeatingType] = useState('full');
  const [seatRows, setSeatRows] = useState(10);
  const [seatColumns, setSeatColumns] = useState(10);
  const [totalSeats, setTotalSeats] = useState(100);
  const [standingCapacity, setStandingCapacity] = useState(0);

  const [showEntrance, setShowEntrance] = useState(true);
  const [showExit, setShowExit] = useState(true);
  const [showStage, setShowStage] = useState(true);
  const [showParking, setShowParking] = useState(true);
  const [parkingZones, setParkingZones] = useState(2);
  const [hasVIPParking, setHasVIPParking] = useState(false);
  const [showFoodCourt, setShowFoodCourt] = useState(false);
  const [showRestrooms, setShowRestrooms] = useState(false);
  const [showFirstAid, setShowFirstAid] = useState(false);
  
  // Mobile responsive state
  const [isMobile, setIsMobile] = useState(false);
  const [isToolsPanelOpen, setIsToolsPanelOpen] = useState(false);
  const [isPropertiesPanelOpen, setIsPropertiesPanelOpen] = useState(false);
  const [canvasScale, setCanvasScale] = useState(1);

  const stallCategories = [
    { id: 'food', label: 'Food', color: '#FF6B6B', icon: '🍔', textColor: 'white' },
    { id: 'clothes', label: 'Clothes', color: '#4ECDC4', icon: '👕', textColor: 'white' },
    { id: 'electronics', label: 'Electronics', color: '#45B7D1', icon: '📱', textColor: 'white' },
    { id: 'toys', label: 'Toys', color: '#FFA07A', icon: '🧸', textColor: 'white' },
    { id: 'general', label: 'General', color: '#98D8C8', icon: '', textColor: 'black' },
    { id: 'premium', label: 'Premium', color: '#F7DC6F', icon: '⭐', textColor: 'black' }
  ];

  const elementTypes = [
    { id: 'parking', label: 'Parking', color: '#4A90E2', width: 140, height: 100, icon: '🅿️' },
    { id: 'restroom', label: 'Restroom', color: '#50C878', width: 80, height: 80, icon: '🚻' },
    { id: 'food', label: 'Food Court', color: '#E67E22', width: 180, height: 120, icon: '🍔' },
    { id: 'firstAid', label: 'First Aid', color: '#E74C3C', width: 80, height: 80, icon: '🏥' },
    { id: 'entrance', label: 'Entrance', color: '#27AE60', width: 120, height: 60, icon: '🚪' },
    { id: 'exit', label: 'Exit', color: '#E74C3C', width: 120, height: 60, icon: '🚪' },
    { id: 'stage', label: 'Stage', color: '#9B59B6', width: 220, height: 100, icon: '🎪' },
    { id: 'info', label: 'Info Booth', color: '#F39C12', width: 100, height: 100, icon: 'ℹ️' }
  ];

  // Check for mobile view
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) {
        setIsToolsPanelOpen(true);
        setIsPropertiesPanelOpen(true);
      } else {
        setIsToolsPanelOpen(false);
        setIsPropertiesPanelOpen(false);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Calculate canvas scale for mobile
  useEffect(() => {
    if (isMobile && canvasContainerRef.current) {
      const containerWidth = canvasContainerRef.current.clientWidth;
      const canvasWidth = imageDimensions.width;
      const scale = Math.min(1, containerWidth / (canvasWidth + 40));
      setCanvasScale(scale);
    } else {
      setCanvasScale(1);
    }
  }, [isMobile, imageDimensions.width]);

  const updateSeatCount = (rows, cols) => {
    const total = rows * cols;
    setTotalSeats(total);
    return total;
  };

  const handleRowsChange = (e) => {
    const rows = parseInt(e.target.value) || 1;
    setSeatRows(rows);
    updateSeatCount(rows, seatColumns);
  };

  const handleColumnsChange = (e) => {
    const cols = parseInt(e.target.value) || 1;
    setSeatColumns(cols);
    updateSeatCount(seatRows, cols);
  };

  const handleTicketTypeChange = (type) => {
    setTicketType(type);
    setHasSeating(type === 'seated');
  };

  const fitImageToContainer = () => {
    if (!backgroundImageUrl) return;
    
    const container = canvasContainerRef.current;
    if (!container) return;
    
    const maxWidth = container.clientWidth - 40;
    const maxHeight = container.clientHeight - 40;
    
    const img = new Image();
    img.onload = () => {
      let width = img.width;
      let height = img.height;
      
      setOriginalImageDimensions({ width, height });
      
      const widthRatio = maxWidth / width;
      const heightRatio = maxHeight / height;
      const scale = Math.min(widthRatio, heightRatio);
      
      width = width * scale;
      height = height * scale;
      
      setImageDimensions({ 
        width: Math.round(width), 
        height: Math.round(height) 
      });
      
      setGridSize(Math.floor(width / 20));
      
      console.log(`Image scaled from ${img.width}x${img.height} to ${width}x${height}`);
    };
    img.src = backgroundImageUrl;
  };

  const resetImageSize = () => {
    if (originalImageDimensions.width > 0 && originalImageDimensions.height > 0) {
      setImageDimensions({
        width: originalImageDimensions.width,
        height: originalImageDimensions.height
      });
      setGridSize(Math.floor(originalImageDimensions.width / 20));
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setBackgroundImage(file);
      setBackgroundImageUrl(url);
      
      const img = new Image();
      img.onload = () => {
        setOriginalImageDimensions({ width: img.width, height: img.height });
        
        setTimeout(() => {
          const container = canvasContainerRef.current;
          if (container) {
            const maxWidth = container.clientWidth - 40;
            const maxHeight = container.clientHeight - 40;
            
            let width = img.width;
            let height = img.height;
            
            const widthRatio = maxWidth / width;
            const heightRatio = maxHeight / height;
            const scale = Math.min(widthRatio, heightRatio);
            
            width = width * scale;
            height = height * scale;
            
            setImageDimensions({ 
              width: Math.round(width), 
              height: Math.round(height) 
            });
            setGridSize(Math.floor(width / 20));
          } else {
            const maxWidth = 800;
            const maxHeight = 1100;
            
            let width = img.width;
            let height = img.height;
            
            if (width > maxWidth || height > maxHeight) {
              const ratio = Math.min(maxWidth / width, maxHeight / height);
              width = width * ratio;
              height = height * ratio;
            }
            
            setImageDimensions({ 
              width: Math.round(width), 
              height: Math.round(height) 
            });
            setGridSize(Math.floor(width / 20));
          }
        }, 100);
      };
      img.src = url;
    }
  };

  const getMousePosition = (e) => {
    const container = document.querySelector('.custom-layout-container');
    if (!container) return { x: 0, y: 0 };
    
    const containerRect = container.getBoundingClientRect();
    const scrollLeft = canvasContainerRef.current.scrollLeft;
    const scrollTop = canvasContainerRef.current.scrollTop;
    
    const x = (e.clientX - containerRect.left + scrollLeft) / canvasScale;
    const y = (e.clientY - containerRect.top + scrollTop) / canvasScale;
    
    return { x, y };
  };

  const handleMouseMove = (e) => {
    if (quickPlaceMode && selectedTool === 'add') {
      const pos = getMousePosition(e);
      setPlacementPreview({
        x: pos.x,
        y: pos.y,
        visible: true
      });
    }
  };

  const addStall = (x, y) => {
    if (selectedTool !== 'add') return;
    
    const snapX = snapToGrid ? Math.round(x / gridSize) * gridSize : x;
    const snapY = snapToGrid ? Math.round(y / gridSize) * gridSize : y;

    const category = stallCategories.find(c => c.id === stallCategory);
    
    const newStall = {
      id: `stall-${Date.now()}-${Math.random()}`,
      type: 'stall',
      category: stallCategory,
      x: snapX,
      y: snapY,
      width: stallSize.width,
      height: stallSize.height,
      number: stalls.length + 1,
      price: stallPrice,
      color: category.color,
      icon: category.icon,
      textColor: category.textColor,
      booked: false,
      description: ''
    };
    
    const newStalls = [...stalls, newStall];
    addToHistory(newStalls, fixedElements);
    setStalls(newStalls);
    setQuickPlaceMode(false);
    setPlacementPreview({ visible: false });
  };

  const addBulkStalls = (x, y) => {
    if (selectedTool !== 'bulk') return;
    
    const newStalls = [];
    const category = stallCategories.find(c => c.id === stallCategory);
    
    const startX = snapToGrid ? Math.round(x / gridSize) * gridSize : x;
    const startY = snapToGrid ? Math.round(y / gridSize) * gridSize : y;
    
    for (let row = 0; row < bulkRows; row++) {
      for (let col = 0; col < bulkCols; col++) {
        const stallX = startX + col * (stallSize.width + bulkSpacing);
        const stallY = startY + row * (stallSize.height + bulkSpacing);
        
        if (stallX + stallSize.width < imageDimensions.width && 
            stallY + stallSize.height < imageDimensions.height) {
          newStalls.push({
            id: `stall-${Date.now()}-${row}-${col}-${Math.random()}`,
            type: 'stall',
            category: stallCategory,
            x: stallX,
            y: stallY,
            width: stallSize.width,
            height: stallSize.height,
            number: stalls.length + newStalls.length + 1,
            price: stallPrice,
            color: category.color,
            icon: category.icon,
            textColor: category.textColor,
            booked: false,
            description: ''
          });
        }
      }
    }
    
    const updatedStalls = [...stalls, ...newStalls];
    addToHistory(updatedStalls, fixedElements);
    setStalls(updatedStalls);
    setSelectedTool('select');
  };

  const addFixedElement = (elementType) => {
    const x = imageDimensions.width / 2 - elementType.width / 2;
    const y = imageDimensions.height / 2 - elementType.height / 2;
    
    const newElement = {
      id: `${elementType.id}-${Date.now()}-${Math.random()}`,
      ...elementType,
      x,
      y
    };
    
    const newElements = [...fixedElements, newElement];
    addToHistory(stalls, newElements);
    setFixedElements(newElements);
  };

  const handleCanvasClick = (e) => {
    if (selectedTool === 'add') {
      const pos = getMousePosition(e);
      addStall(pos.x, pos.y);
    } else if (selectedTool === 'bulk') {
      const pos = getMousePosition(e);
      addBulkStalls(pos.x, pos.y);
    }
  };

  const handleDragStop = (id, d, type) => {
    let newX = d.x;
    let newY = d.y;
    
    if (snapToGrid) {
      newX = Math.round(newX / gridSize) * gridSize;
      newY = Math.round(newY / gridSize) * gridSize;
    }
    
    if (type === 'stall') {
      const updatedStalls = stalls.map(s => 
        s.id === id ? { ...s, x: newX, y: newY } : s
      );
      addToHistory(updatedStalls, fixedElements);
      setStalls(updatedStalls);
    } else {
      const updatedElements = fixedElements.map(e => 
        e.id === id ? { ...e, x: newX, y: newY } : e
      );
      addToHistory(stalls, updatedElements);
      setFixedElements(updatedElements);
    }
  };

  const handleResizeStop = (id, direction, ref, delta, position, type) => {
    const newWidth = parseInt(ref.style.width);
    const newHeight = parseInt(ref.style.height);
    
    if (type === 'stall') {
      const updatedStalls = stalls.map(s => 
        s.id === id ? { 
          ...s, 
          width: newWidth, 
          height: newHeight,
          x: position.x,
          y: position.y
        } : s
      );
      addToHistory(updatedStalls, fixedElements);
      setStalls(updatedStalls);
    } else {
      const updatedElements = fixedElements.map(e => 
        e.id === id ? { 
          ...e, 
          width: newWidth, 
          height: newHeight,
          x: position.x,
          y: position.y
        } : e
      );
      addToHistory(stalls, updatedElements);
      setFixedElements(updatedElements);
    }
  };

  const deleteSelected = () => {
    if (!selectedId) return;
    
    const newStalls = stalls.filter(s => s.id !== selectedId);
    const newElements = fixedElements.filter(e => e.id !== selectedId);
    addToHistory(newStalls, newElements);
    setStalls(newStalls);
    setFixedElements(newElements);
    setSelectedId(null);
  };

  const addToHistory = (newStalls, newElements) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push({ 
      stalls: JSON.parse(JSON.stringify(newStalls)), 
      elements: JSON.parse(JSON.stringify(newElements)) 
    });
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const undo = () => {
    if (historyIndex > 0) {
      const prev = history[historyIndex - 1];
      setStalls(prev.stalls || []);
      setFixedElements(prev.elements || []);
      setHistoryIndex(historyIndex - 1);
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      const next = history[historyIndex + 1];
      setStalls(next.stalls || []);
      setFixedElements(next.elements || []);
      setHistoryIndex(historyIndex + 1);
    }
  };

  const fitToView = () => {
    if (canvasContainerRef.current) {
      const container = canvasContainerRef.current;
      container.scrollLeft = 0;
      container.scrollTop = 0;
    }
  };

  const toggleQuickPlace = () => {
    setQuickPlaceMode(!quickPlaceMode);
    if (selectedTool !== 'add') {
      setSelectedTool('add');
    }
  };

  const duplicateSelected = () => {
    if (!selectedId) return;
    
    const selected = stalls.find(s => s.id === selectedId);
    if (selected) {
      const newStall = {
        ...selected,
        id: `stall-${Date.now()}-${Math.random()}`,
        number: stalls.length + 1,
        x: selected.x + selected.width + 20,
        y: selected.y,
        booked: false,
        description: selected.description || ''
      };
      const newStalls = [...stalls, newStall];
      addToHistory(newStalls, fixedElements);
      setStalls(newStalls);
      setSelectedId(newStall.id);
    }
  };

  const alignSelected = (direction) => {
    if (!selectedId) return;
    
    const selected = stalls.find(s => s.id === selectedId);
    if (!selected) return;
    
    let newX = selected.x;
    let newY = selected.y;
    
    switch(direction) {
      case 'left':
        newX = 0;
        break;
      case 'right':
        newX = imageDimensions.width - selected.width;
        break;
      case 'top':
        newY = 0;
        break;
      case 'bottom':
        newY = imageDimensions.height - selected.height;
        break;
      case 'center':
        newX = (imageDimensions.width / 2) - (selected.width / 2);
        newY = (imageDimensions.height / 2) - (selected.height / 2);
        break;
      default:
        break;
    }
    
    const updatedStalls = stalls.map(s => 
      s.id === selectedId ? { ...s, x: newX, y: newY } : s
    );
    addToHistory(updatedStalls, fixedElements);
    setStalls(updatedStalls);
  };

  const compressImage = (base64String) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = base64String;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        
        const maxDimension = 800;
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = (height * maxDimension) / width;
            width = maxDimension;
          } else {
            width = (width * maxDimension) / height;
            height = maxDimension;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        const compressed = canvas.toDataURL('image/jpeg', 0.8);
        resolve(compressed);
      };
    });
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      
      console.log("========== SAVING CUSTOM LAYOUT ==========");
      console.log("Mode:", isEditMode ? "EDIT" : "CREATE");
      console.log("Event ID:", eventId);
      console.log("Ticket Type:", ticketType);
      
      let formData;
      
      if (isEditMode && eventId) {
        formData = pendingFormData || {};
        console.log("Edit mode - formData:", formData);
      } else {
        const storedData = sessionStorage.getItem('pendingEventData');
        if (!storedData) {
          alert("No event data found");
          navigate('/addevent');
          return;
        }
        formData = JSON.parse(storedData);
      }
      
      let blueprintBase64 = null;
      if (backgroundImage) {
        try {
          if (backgroundImage instanceof File) {
            const rawBase64 = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(backgroundImage);
            });
            
            blueprintBase64 = await compressImage(rawBase64);
            console.log("✅ Image converted and compressed");
          } else if (typeof backgroundImage === 'string' && backgroundImage.startsWith('data:')) {
            blueprintBase64 = backgroundImage;
          }
        } catch (error) {
          console.error("Error converting image:", error);
        }
      }

      const formattedStalls = stalls.map((stall, index) => ({
        id: stall.id,
        number: index + 1,
        stallNumber: index + 1,
        x: Number(stall.x),
        y: Number(stall.y),
        width: Number(stall.width),
        height: Number(stall.height),
        price: Number(stall.price) || stallPrice,
        category: stall.category || stallCategory,
        color: stall.color,
        icon: stall.icon,
        textColor: stall.textColor,
        booked: stall.booked || false,
        description: stall.description || ''
      }));

      const formattedFixedElements = fixedElements.map(el => ({
        id: el.id,
        label: el.label,
        x: Number(el.x),
        y: Number(el.y),
        width: Number(el.width),
        height: Number(el.height),
        color: el.color,
        icon: el.icon || '📍'
      }));

      const layoutObject = {
        stalls: formattedStalls,
        fixedElements: formattedFixedElements,
        backgroundImage: blueprintBase64,
        imageDimensions: {
          width: imageDimensions.width,
          height: imageDimensions.height
        },
        gridSize: gridSize,
        stallCategories: stallCategories,
        version: "1.0",
        
        ticketType: ticketType,
        hasSeating: hasSeating,
        seatingType: seatingType,
        seatRows: seatRows,
        seatColumns: seatColumns,
        totalSeats: ticketType === 'seated' ? totalSeats : 0,
        standingCapacity: standingCapacity,
        
        ticketBookings: {
          seated: [],
          standing: []
        }
      };

      const customLayoutDataString = JSON.stringify(layoutObject);
      
      console.log("Custom Layout Data (stringified length):", customLayoutDataString.length);

      const formatDate = (dateString) => {
        if (!dateString) return new Date().toISOString().split('T')[0];
        const date = new Date(dateString);
        return date.toISOString().split('T')[0];
      };

      const apiEventData = {
        ...(isEditMode && eventId ? { eventId: eventId } : {}),
        
        eventName: formData.name || formData.eventName || "Custom Event",
        eventDate: formatDate(formData.date || formData.eventDate || new Date()),
        spansour: formData.spansaour || formData.spansour || "Unknown Sponsor",
        place: formData.venue || formData.place || "Unknown Venue",
        budget: Number(formData.budget) || 0,
        highlights: formData.highlights || "",
        
        stallPrice: Number(formData.stallPrice) || stallPrice,
        ticketPrice: Number(formData.ticketPrice) || 1000,
        standingCapacity: standingCapacity,
        
        stallPattern: "custom",
        isCustomLayout: true,
        customLayoutData: customLayoutDataString,
        blueprintImage: blueprintBase64,
        
        ticketType: ticketType,
        hasSeating: ticketType === 'seated',
        seatingType: seatingType,
        seatRows: seatRows,
        seatColumns: seatColumns,
        totalSeats: ticketType === 'seated' ? totalSeats : 0,
        
        showEntrance: showEntrance,
        showExit: showExit,
        showStage: showStage,
        showParking: showParking,
        parkingZones: parkingZones,
        hasVIPParking: hasVIPParking,
        showFoodCourt: showFoodCourt,
        showRestrooms: showRestrooms,
        showFirstAid: showFirstAid
      };

      console.log("Sending to PHP API...");

      let response;
      if (isEditMode && eventId) {
        response = await fetch(`${API_BASE_URL}/events.php`, {
          method: "PUT",
          headers: { 
            "Content-Type": "application/json"
          },
          body: JSON.stringify(apiEventData),
        });
      } else {
        response = await fetch(`${API_BASE_URL}/events.php`, {
          method: "POST",
          headers: { 
            "Content-Type": "application/json"
          },
          body: JSON.stringify(apiEventData),
        });
      }

      const responseText = await response.text();
      console.log("Raw response length:", responseText.length);
      
      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
        console.error("Failed to parse JSON. Raw response (first 500 chars):", responseText.substring(0, 500));
        throw new Error(`Server returned invalid JSON.`);
      }
      
      if (!response.ok) {
        throw new Error(responseData.error || "Failed to save event");
      }
      
      console.log("✅ Event saved successfully:", responseData);
      
      if (!isEditMode) {
        sessionStorage.removeItem('pendingEventData');
      }
      
      const ticketTypeMessage = ticketType === 'seated' ? 'Seated' : (ticketType === 'standing' ? 'Standing' : 'No Tickets');
      alert(`✅ Custom layout ${isEditMode ? 'updated' : 'created'} successfully!\n• ${formattedStalls.length} stalls\n• ${formattedFixedElements.length} elements\n• Ticket Type: ${ticketTypeMessage}`);
      navigate('/adminDashboard');

    } catch (error) {
      console.error("❌ Error saving event:", error);
      alert("Error: " + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/adminDashboard');
  };

  useEffect(() => {
    const loadData = async () => {
      if (isEditMode && eventId) {
        try {
          setIsLoading(true);
          
          const eventResponse = await axios.get(`${API_BASE_URL}/events.php?eventId=${eventId}`);
          const eventDetails = eventResponse.data;
          console.log("✅ Loaded event details:", eventDetails);
          
          const loadedTicketType = eventDetails.ticketType || (eventDetails.hasSeating ? 'seated' : 'standing');
          setTicketType(loadedTicketType);
          setHasSeating(loadedTicketType === 'seated');
          setSeatingType(eventDetails.seatingType || 'full');
          setSeatRows(eventDetails.seatRows || 10);
          setSeatColumns(eventDetails.seatColumns || 10);
          setTotalSeats(eventDetails.totalSeats || 100);
          setStandingCapacity(eventDetails.standingCapacity || 0);
          
          setShowEntrance(eventDetails.showEntrance !== undefined ? eventDetails.showEntrance : true);
          setShowExit(eventDetails.showExit !== undefined ? eventDetails.showExit : true);
          setShowStage(eventDetails.showStage !== undefined ? eventDetails.showStage : true);
          setShowParking(eventDetails.showParking !== undefined ? eventDetails.showParking : true);
          setParkingZones(eventDetails.parkingZones || 2);
          setHasVIPParking(eventDetails.hasVIPParking || false);
          setShowFoodCourt(eventDetails.showFoodCourt || false);
          setShowRestrooms(eventDetails.showRestrooms || false);
          setShowFirstAid(eventDetails.showFirstAid || false);
          
          if (eventDetails.customLayoutData) {
            console.log("Raw customLayoutData from DB type:", typeof eventDetails.customLayoutData);
            
            let layout;
            if (typeof eventDetails.customLayoutData === 'string') {
              try {
                layout = JSON.parse(eventDetails.customLayoutData);
                console.log("Parsed layout from string successfully");
              } catch (parseError) {
                console.error("Failed to parse customLayoutData:", parseError);
                layout = {};
              }
            } else {
              layout = eventDetails.customLayoutData;
            }
            
            if (layout.stalls && Array.isArray(layout.stalls)) {
              console.log("Loading stalls:", layout.stalls.length);
              const loadedStalls = layout.stalls.map((stall, idx) => ({
                ...stall,
                number: stall.number || idx + 1,
                booked: stall.booked || false,
                description: stall.description || ''
              }));
              setStalls(loadedStalls);
            }
            if (layout.fixedElements && Array.isArray(layout.fixedElements)) {
              console.log("Loading fixedElements:", layout.fixedElements.length);
              setFixedElements(layout.fixedElements);
            }
            if (layout.imageDimensions) {
              console.log("Loading imageDimensions:", layout.imageDimensions);
              setImageDimensions(layout.imageDimensions);
            }
            if (layout.gridSize) {
              setGridSize(layout.gridSize);
            }
            if (layout.backgroundImage) {
              console.log("Loading background image");
              setBackgroundImageUrl(layout.backgroundImage);
              setBackgroundImage(layout.backgroundImage);
            }
            if (layout.ticketType) {
              setTicketType(layout.ticketType);
              setHasSeating(layout.ticketType === 'seated');
            }
          } else {
            console.log("No customLayoutData found for this event");
          }
          
          const eventData = {
            eventId: eventDetails.eventId,
            name: eventDetails.eventName,
            date: eventDetails.eventDate,
            spansaour: eventDetails.spansour,
            venue: eventDetails.place,
            budget: eventDetails.budget,
            highlights: eventDetails.highlights,
            stallPrice: eventDetails.stallPrice,
            ticketPrice: eventDetails.ticketPrice,
            standingCapacity: eventDetails.standingCapacity,
            ticketType: loadedTicketType,
            hasSeating: loadedTicketType === 'seated',
            seatingType: eventDetails.seatingType,
            seatRows: eventDetails.seatRows,
            seatColumns: eventDetails.seatColumns,
            totalSeats: eventDetails.totalSeats,
            showEntrance: eventDetails.showEntrance,
            showExit: eventDetails.showExit,
            showStage: eventDetails.showStage,
            showParking: eventDetails.showParking,
            parkingZones: eventDetails.parkingZones,
            hasVIPParking: eventDetails.hasVIPParking,
            showFoodCourt: eventDetails.showFoodCourt,
            showRestrooms: eventDetails.showRestrooms,
            showFirstAid: eventDetails.showFirstAid,
            isEditMode: true
          };
          
          sessionStorage.setItem('pendingEventData', JSON.stringify(eventData));
          setPendingFormData(eventData);
          
          setHistory([]);
          setHistoryIndex(-1);
          
        } catch (error) {
          console.error("Error loading event:", error);
          alert("Failed to load event: " + (error.response?.data?.error || error.message));
          navigate('/adminDashboard');
        } finally {
          setIsLoading(false);
        }
      } else {
        const storedData = sessionStorage.getItem('pendingEventData');
        if (storedData) {
          const parsedData = JSON.parse(storedData);
          setPendingFormData(parsedData);
          
          setTicketType(parsedData.ticketType || 'seated');
          setHasSeating(parsedData.hasSeating !== undefined ? parsedData.hasSeating : true);
          setSeatingType(parsedData.seatingType || 'full');
          setSeatRows(parsedData.seatRows || 10);
          setSeatColumns(parsedData.seatColumns || 10);
          setTotalSeats(parsedData.totalSeats || 100);
          setStandingCapacity(parsedData.standingCapacity || 0);
          
          setShowEntrance(parsedData.showEntrance !== undefined ? parsedData.showEntrance : true);
          setShowExit(parsedData.showExit !== undefined ? parsedData.showExit : true);
          setShowStage(parsedData.showStage !== undefined ? parsedData.showStage : true);
          setShowParking(parsedData.showParking !== undefined ? parsedData.showParking : true);
          setParkingZones(parsedData.parkingZones || 2);
          setHasVIPParking(parsedData.hasVIPParking || false);
          setShowFoodCourt(parsedData.showFoodCourt || false);
          setShowRestrooms(parsedData.showRestrooms || false);
          setShowFirstAid(parsedData.showFirstAid || false);
          
          setHistory([]);
          setHistoryIndex(-1);
          
        } else {
          alert("No event data found");
          navigate('/addevent');
        }
        setIsLoading(false);
      }
    };

    loadData();
  }, [isEditMode, eventId, navigate]);

  useEffect(() => {
    if ((stalls.length > 0 || fixedElements.length > 0) && history.length === 0 && historyIndex === -1) {
      addToHistory(stalls, fixedElements);
    }
  }, [stalls, fixedElements, history.length, historyIndex]);

  useEffect(() => {
    if (!backgroundImageUrl) return;
    
    const handleResize = () => {
      fitImageToContainer();
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [backgroundImageUrl]);

  if (isLoading) {
    return (
      <>
        <Navbar />
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading layout designer...</p>
        </div>
      </>
    );
  }

  return (
    <>
      <div className={`layout-builder ${isMobile ? 'mobile-view' : 'desktop-view'}`}>
        <div className="builder-header">
          <div className="header-left">
            <button className="back-btn" onClick={handleBack} title="Back to Dashboard">
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <h1>
              {isEditMode ? '✏️ Edit Layout' : '🎨 Custom Layout'}
            </h1>
            {!isMobile && (
              <>
                <span className="event-name">
                  {pendingFormData?.name || eventName || 'New Event'}
                </span>
                {isEditMode && (
                  <span className="edit-badge">Editing Mode</span>
                )}
              </>
            )}
          </div>
          <div className="header-actions">
            {isMobile && (
              <>
                <button 
                  className="mobile-panel-toggle tools-toggle"
                  onClick={() => setIsToolsPanelOpen(!isToolsPanelOpen)}
                  title="Tools"
                >
                  <FontAwesomeIcon icon={faBars} />
                </button>
                <button 
                  className="mobile-panel-toggle properties-toggle"
                  onClick={() => setIsPropertiesPanelOpen(!isPropertiesPanelOpen)}
                  title="Properties"
                >
                  <FontAwesomeIcon icon={faInfoCircle} />
                </button>
              </>
            )}
            <button className="btn-icon" onClick={fitImageToContainer} title="Fit Image to Container">
              <FontAwesomeIcon icon={faArrowsAltH} />
            </button>
            <button className="btn-icon" onClick={resetImageSize} title="Reset Image Size" disabled={!backgroundImageUrl}>
              <FontAwesomeIcon icon={faRotateRight} />
            </button>
            <button className="btn-icon" onClick={fitToView} title="Fit to View">
              <FontAwesomeIcon icon={faExpand} />
            </button>
            <button 
              className="btn-save" 
              onClick={handleSave} 
              disabled={stalls.length === 0 || isSaving}
            >
              <FontAwesomeIcon icon={faSave} />
              {!isMobile && (isSaving ? 'Saving...' : isEditMode ? 'Update' : 'Save')}
              {!isMobile && !isSaving && ` (${stalls.length})`}
            </button>
          </div>
        </div>

        <div className="builder-main" onMouseMove={handleMouseMove}>
          {/* Left Tools Panel */}
          <div className={`tools-panel ${isMobile ? (isToolsPanelOpen ? 'open' : 'closed') : ''}`}>
            {isMobile && (
              <div className="panel-header">
                <h3>Tools & Settings</h3>
                <button className="close-panel" onClick={() => setIsToolsPanelOpen(false)}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            )}
            <div className="quick-stats">
              <div className="stat-item">
                <span className="stat-label">Stalls</span>
                <span className="" style={{color:"green", fontWeight:"700"}}>{stalls.length}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Selected</span>
                <span className="" style={{color:"red", fontWeight:"700"}}>{selectedId ? '1' : '0'}</span>
              </div>
            </div>

            <div className="tool-group">
              <h3>Tools</h3>
              <div className="tool-buttons">
                <button 
                  className={`tool-icon-btn ${selectedTool === 'select' ? 'active' : ''}`}
                  onClick={() => setSelectedTool('select')}
                  title="Select & Move"
                >
                  <FontAwesomeIcon icon={faMousePointer} />
                </button>
                <button 
                  className={`tool-icon-btn ${selectedTool === 'add' ? 'active' : ''}`}
                  onClick={() => setSelectedTool('add')}
                  title="Add Stall"
                >
                  <FontAwesomeIcon icon={faPlus} />
                </button>
                <button 
                  className={`tool-icon-btn ${selectedTool === 'bulk' ? 'active' : ''}`}
                  onClick={() => setSelectedTool('bulk')}
                  title="Bulk Add"
                >
                  <FontAwesomeIcon icon={faCopy} />
                </button>
                <button 
                  className="tool-icon-btn danger"
                  onClick={deleteSelected}
                  disabled={!selectedId}
                  title="Delete"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
                <button 
                  className="tool-icon-btn"
                  onClick={duplicateSelected}
                  disabled={!selectedId}
                  title="Duplicate"
                >
                  <FontAwesomeIcon icon={faCopy} />
                </button>
                <button 
                  className="tool-icon-btn"
                  onClick={undo}
                  disabled={historyIndex <= 0}
                  title="Undo"
                >
                  <FontAwesomeIcon icon={faUndo} />
                </button>
                <button 
                  className="tool-icon-btn"
                  onClick={redo}
                  disabled={historyIndex >= history.length - 1}
                  title="Redo"
                >
                  <FontAwesomeIcon icon={faRedo} />
                </button>
                <button 
                  className={`tool-icon-btn ${quickPlaceMode ? 'active magic' : ''}`}
                  onClick={toggleQuickPlace}
                  title="Quick Place"
                >
                  <FontAwesomeIcon icon={faArrowsAlt} />
                </button>
              </div>
            </div>

            {selectedId && (
              <div className="tool-group">
                <h3>Quick Align</h3>
                <div className="tool-buttons">
                  <button className="tool-icon-btn small" onClick={() => alignSelected('left')} title="Left">⬅️</button>
                  <button className="tool-icon-btn small" onClick={() => alignSelected('center')} title="Center">⏺️</button>
                  <button className="tool-icon-btn small" onClick={() => alignSelected('right')} title="Right">➡️</button>
                  <button className="tool-icon-btn small" onClick={() => alignSelected('top')} title="Top">⬆️</button>
                  <button className="tool-icon-btn small" onClick={() => alignSelected('bottom')} title="Bottom">⬇️</button>
                </div>
              </div>
            )}

            <div className="tool-group">
              <h3>Blueprint</h3>
              <button className="upload-btn-small" onClick={() => fileInputRef.current.click()}>
                <FontAwesomeIcon icon={faUpload} />
                {backgroundImage ? 'Change' : 'Upload'}
              </button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                style={{ display: 'none' }}
              />
              
              {backgroundImage && (
                <>
                  <div className="setting-row">
                    <label>Opacity: {Math.round(imageOpacity * 100)}%</label>
                    <input 
                      type="range" 
                      min="0.1" 
                      max="1" 
                      step="0.1"
                      value={imageOpacity}
                      onChange={(e) => setImageOpacity(parseFloat(e.target.value))}
                    />
                  </div>
                  <button 
                    className="tool-icon-btn small full-width"
                    onClick={() => setShowBlueprint(!showBlueprint)}
                  >
                    <FontAwesomeIcon icon={showBlueprint ? faEye : faEyeSlash} />
                    {showBlueprint ? 'Hide' : 'Show'}
                  </button>
                  {!isMobile && originalImageDimensions.width > 0 && (
                    <div className="image-info">
                      <small>Original: {originalImageDimensions.width}x{originalImageDimensions.height}</small>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="tool-group">
              <h3>Category</h3>
              <div className="category-quick-select">
                {stallCategories.map(cat => (
                  <button
                    key={cat.id}
                    className={`cat-dot ${stallCategory === cat.id ? 'active' : ''}`}
                    style={{ backgroundColor: cat.color, color: cat.textColor }}
                    onClick={() => setStallCategory(cat.id)}
                    title={cat.label}
                  >
                    {cat.icon}
                  </button>
                ))}
              </div>
            </div>

            <div className="tool-group">
              <h3>Size</h3>
              <div className="size-controls">
                <input 
                  type="number" 
                  value={stallSize.width}
                  onChange={(e) => setStallSize({...stallSize, width: Number(e.target.value)})}
                  min="30"
                  max="200"
                  step="5"
                  placeholder="W"
                />
                <span>×</span>
                <input 
                  type="number" 
                  value={stallSize.height}
                  onChange={(e) => setStallSize({...stallSize, height: Number(e.target.value)})}
                  min="30"
                  max="200"
                  step="5"
                  placeholder="H"
                />
              </div>
            </div>

            <div className="tool-group">
              <h3>Price (₹)</h3>
              <input 
                type="number" 
                className="price-input"
                value={stallPrice}
                onChange={(e) => setStallPrice(Number(e.target.value))}
                step="100"
                min="0"
              />
            </div>

            {selectedTool === 'bulk' && (
              <div className="tool-group highlight">
                <h3>Bulk Settings</h3>
                <div className="setting-row">
                  <label>Rows: {bulkRows}</label>
                  <input 
                    type="range" 
                    value={bulkRows}
                    onChange={(e) => setBulkRows(Number(e.target.value))}
                    min="1"
                    max="10"
                  />
                </div>
                <div className="setting-row">
                  <label>Cols: {bulkCols}</label>
                  <input 
                    type="range" 
                    value={bulkCols}
                    onChange={(e) => setBulkCols(Number(e.target.value))}
                    min="1"
                    max="10"
                  />
                </div>
                <div className="setting-row">
                  <label>Spacing: {bulkSpacing}px</label>
                  <input 
                    type="range" 
                    value={bulkSpacing}
                    onChange={(e) => setBulkSpacing(Number(e.target.value))}
                    min="0"
                    max="50"
                  />
                </div>
                <p className="hint">Click to place {bulkRows}×{bulkCols} stalls</p>
              </div>
            )}

            <div className="tool-group">
              <h3>Elements</h3>
              <div className="elements-grid">
                {elementTypes.map(el => (
                  <button
                    key={el.id}
                    className="element-btn-small"
                    onClick={() => addFixedElement(el)}
                    style={{ borderLeftColor: el.color }}
                  >
                    <span className="element-icon">{el.icon}</span>
                    {!isMobile && <span className="element-label">{el.label}</span>}
                  </button>
                ))}
              </div>
            </div>

            <div className="tool-group">
              <h3>Grid</h3>
              <label className="checkbox-label">
                <input 
                  type="checkbox" 
                  checked={showGrid}
                  onChange={(e) => setShowGrid(e.target.checked)}
                />
                Show Grid
              </label>
              <label className="checkbox-label">
                <input 
                  type="checkbox" 
                  checked={snapToGrid}
                  onChange={(e) => setSnapToGrid(e.target.checked)}
                />
                Snap to Grid
              </label>
              <div className="setting-row">
                <label>Size: {gridSize}px</label>
                <input 
                  type="range" 
                  value={gridSize}
                  onChange={(e) => setGridSize(Number(e.target.value))}
                  min="20"
                  max="100"
                  step="5"
                />
              </div>
            </div>
          </div>

          {/* Canvas Area */}
          <div 
            className="canvas-area"
            ref={canvasContainerRef}
            style={{
              flex: 1,
              background: '#1a2634',
              overflow: 'auto',
              position: 'relative',
              padding: isMobile ? '10px' : '20px'
            }}
          >
            <div 
              className="custom-layout-container"
              style={{ 
                width: imageDimensions.width * canvasScale,
                height: imageDimensions.height * canvasScale,
                position: 'relative',
                background: '#ffffff',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: 'inset 0 0 20px rgba(0,0,0,0.1)',
                border: '2px solid #34495e',
                margin: '0 auto',
                transform: `scale(${canvasScale})`,
                transformOrigin: 'top center'
              }}
              onClick={handleCanvasClick}
            >
              {backgroundImageUrl && showBlueprint && (
                <img 
                  src={backgroundImageUrl} 
                  alt="Venue Blueprint"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    opacity: imageOpacity,
                    pointerEvents: 'none',
                    zIndex: 1
                  }}
                />
              )}

              {showGrid && (
                <div 
                  className="blueprint-grid"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundImage: 'linear-gradient(to right, rgba(200,200,200,0.2) 1px, transparent 1px), linear-gradient(to bottom, rgba(200,200,200,0.2) 1px, transparent 1px)',
                    backgroundSize: `${gridSize}px ${gridSize}px`,
                    pointerEvents: 'none',
                    zIndex: 2
                  }}
                />
              )}

              {!isMobile && (
                <>
                  <div className="directional north" style={{ position: 'absolute', top: '10px', left: '50%', transform: 'translateX(-50%)', zIndex: 10, color: '#2c3e50', fontWeight: 700, fontSize: '0.8rem', opacity: 0.5 }}>NORTH</div>
                  <div className="directional south" style={{ position: 'absolute', bottom: '10px', left: '50%', transform: 'translateX(-50%)', zIndex: 10, color: '#2c3e50', fontWeight: 700, fontSize: '0.8rem', opacity: 0.5 }}>SOUTH</div>
                  <div className="directional east" style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', zIndex: 10, color: '#2c3e50', fontWeight: 700, fontSize: '0.8rem', opacity: 0.5 }}>EAST</div>
                  <div className="directional west" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', zIndex: 10, color: '#2c3e50', fontWeight: 700, fontSize: '0.8rem', opacity: 0.5 }}>WEST</div>
                </>
              )}

              {placementPreview.visible && selectedTool === 'add' && (
                <div
                  style={{
                    position: 'absolute',
                    left: placementPreview.x,
                    top: placementPreview.y,
                    width: stallSize.width,
                    height: stallSize.height,
                    backgroundColor: stallCategories.find(c => c.id === stallCategory)?.color,
                    opacity: 0.3,
                    border: '2px dashed #000',
                    borderRadius: '8px',
                    pointerEvents: 'none',
                    zIndex: 20
                  }}
                />
              )}

              {fixedElements.map(el => (
                <Rnd
                  key={el.id}
                  size={{ width: el.width, height: el.height }}
                  position={{ x: el.x, y: el.y }}
                  onDragStop={(e, d) => handleDragStop(el.id, d, 'element')}
                  onResizeStop={(e, direction, ref, delta, position) => 
                    handleResizeStop(el.id, direction, ref, delta, position, 'element')
                  }
                  onClick={() => setSelectedId(el.id)}
                  bounds="parent"
                  dragGrid={snapToGrid ? [gridSize, gridSize] : undefined}
                  enableDragging={selectedTool === 'select'}
                  enableResizing={selectedTool === 'select'}
                  style={{
                    border: selectedId === el.id ? '3px solid #ff0000' : '2px solid #333',
                    borderRadius: '8px',
                    backgroundColor: el.color,
                    opacity: 0.9,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: `${Math.min(14, el.width / 6)}px`,
                    fontWeight: 'bold',
                    cursor: selectedTool === 'select' ? 'move' : 'default',
                    boxShadow: selectedId === el.id ? '0 0 20px rgba(255,0,0,0.5)' : '0 2px 8px rgba(0,0,0,0.2)',
                    zIndex: selectedId === el.id ? 100 : 10,
                    padding: '2px'
                  }}
                >
                  <span style={{ fontSize: `${Math.min(20, el.width / 4)}px`, marginRight: '5px' }}>{el.icon}</span>
                  {!isMobile && <span style={{ fontSize: `${Math.min(14, el.width / 6)}px` }}>{el.label}</span>}
                  {selectedId === el.id && (
                    <button 
                      className="delete-btn-small"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteSelected();
                      }}
                    >
                      ×
                    </button>
                  )}
                </Rnd>
              ))}

              {stalls.map(stall => {
                const numberFontSize = Math.max(8, Math.min(16, stall.width / 5));
                const priceFontSize = Math.max(7, Math.min(12, stall.width / 6));
                const iconFontSize = Math.max(12, Math.min(20, stall.width / 4));
                
                return (
                  <Rnd
                    key={stall.id}
                    size={{ width: stall.width, height: stall.height }}
                    position={{ x: stall.x, y: stall.y }}
                    onDragStop={(e, d) => handleDragStop(stall.id, d, 'stall')}
                    onResizeStop={(e, direction, ref, delta, position) => 
                      handleResizeStop(stall.id, direction, ref, delta, position, 'stall')
                    }
                    onClick={() => setSelectedId(stall.id)}
                    bounds="parent"
                    dragGrid={snapToGrid ? [gridSize, gridSize] : undefined}
                    enableDragging={selectedTool === 'select'}
                    enableResizing={selectedTool === 'select'}
                    style={{
                      border: selectedId === stall.id ? '3px solid #ff0000' : '2px solid #28a745',
                      borderRadius: '8px',
                      backgroundColor: stall.color,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: stall.textColor,
                      cursor: selectedTool === 'select' ? 'move' : 'pointer',
                      boxShadow: selectedId === stall.id ? '0 0 20px rgba(255,0,0,0.5)' : '0 4px 8px rgba(40,167,69,0.3)',
                      transition: 'all 0.2s',
                      zIndex: selectedId === stall.id ? 100 : 20,
                      padding: '2px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      textAlign: 'center'
                    }}
                  >
                    <div style={{ fontSize: `${numberFontSize}px`, fontWeight: 'bold' }}>#{stall.number}</div>
                    <div style={{ fontSize: `${priceFontSize}px` }}>₹{stall.price}</div>
                    <div style={{ fontSize: `${iconFontSize}px` }}>{stall.icon}</div>
                    {selectedId === stall.id && (
                      <button 
                        className="delete-btn-small"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteSelected();
                        }}
                      >
                        ×
                      </button>
                    )}
                  </Rnd>
                );
              })}
            </div>
          </div>

          {/* Right Properties Panel */}
          <div className={`properties-panel ${isMobile ? (isPropertiesPanelOpen ? 'open' : 'closed') : ''}`}>
            {isMobile && (
              <div className="panel-header">
                <h3>Properties</h3>
                <button className="close-panel" onClick={() => setIsPropertiesPanelOpen(false)}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            )}
            <h3>Properties</h3>
            
            {/* Ticket Type Selection */}
            <div className="property-section ticket-type-section">
              <h4>
                <FontAwesomeIcon icon={faTicketAlt} style={{ marginRight: '8px' }} />
                Ticket Config
              </h4>
              
              <div className="ticket-type-options">
                <div 
                  className={`ticket-option ${ticketType === 'seated' ? 'active' : ''}`}
                  onClick={() => handleTicketTypeChange('seated')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '10px',
                    borderRadius: '8px',
                    border: ticketType === 'seated' ? '2px solid #28a745' : '1px solid #3d566e',
                    background: ticketType === 'seated' ? '#1a472a' : '#34495e',
                    cursor: 'pointer',
                    marginBottom: '8px',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ fontSize: '20px' }}>🪑</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 'bold', fontSize: isMobile ? '12px' : '14px' }}>Seated</div>
                    {!isMobile && <div style={{ fontSize: '11px', opacity: 0.8 }}>Assigned seating</div>}
                  </div>
                  {ticketType === 'seated' && <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#28a745', fontSize: '14px' }} />}
                </div>
                
                <div 
                  className={`ticket-option ${ticketType === 'standing' ? 'active' : ''}`}
                  onClick={() => handleTicketTypeChange('standing')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '10px',
                    borderRadius: '8px',
                    border: ticketType === 'standing' ? '2px solid #ffc107' : '1px solid #3d566e',
                    background: ticketType === 'standing' ? '#3a3a1a' : '#34495e',
                    cursor: 'pointer',
                    marginBottom: '8px',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ fontSize: '20px' }}>🎫</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 'bold', fontSize: isMobile ? '12px' : '14px' }}>Standing</div>
                    {!isMobile && <div style={{ fontSize: '11px', opacity: 0.8 }}>General admission</div>}
                  </div>
                  {ticketType === 'standing' && <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#ffc107', fontSize: '14px' }} />}
                </div>
                
                <div 
                  className={`ticket-option ${ticketType === 'none' ? 'active' : ''}`}
                  onClick={() => handleTicketTypeChange('none')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '10px',
                    borderRadius: '8px',
                    border: ticketType === 'none' ? '2px solid #dc3545' : '1px solid #3d566e',
                    background: ticketType === 'none' ? '#3a1a1a' : '#34495e',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ fontSize: '20px' }}>🚫</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 'bold', fontSize: isMobile ? '12px' : '14px' }}>No Tickets</div>
                    {!isMobile && <div style={{ fontSize: '11px', opacity: 0.8 }}>Free entry</div>}
                  </div>
                  {ticketType === 'none' && <FontAwesomeIcon icon={faBan} style={{ color: '#dc3545', fontSize: '14px' }} />}
                </div>
              </div>
            </div>

            {/* Seating Configuration */}
            {ticketType === 'seated' && (
              <div className="property-section seating-section">
                <h4>
                  <FontAwesomeIcon icon={faChair} style={{ marginRight: '8px' }} />
                  Seating
                </h4>
                
                <div className="property-row">
                  <label>Type</label>
                  <select 
                    value={seatingType}
                    onChange={(e) => setSeatingType(e.target.value)}
                  >
                    <option value="full">Full Seating</option>
                    <option value="partial">Partial Seating</option>
                    <option value="theater">Theater Style</option>
                    <option value="classroom">Classroom Style</option>
                  </select>
                </div>

                <div className="property-row dimension-row">
                  <div className="dimension-input-group" style={{ display: 'flex', gap: '10px' }}>
                    <div className="dimension-field" style={{ flex: 1 }}>
                      <label>Rows</label>
                      <input 
                        type="number" 
                        value={seatRows}
                        onChange={handleRowsChange}
                        min="1"
                        max="50"
                      />
                    </div>
                    <div className="dimension-field" style={{ flex: 1 }}>
                      <label>Cols</label>
                      <input 
                        type="number" 
                        value={seatColumns}
                        onChange={handleColumnsChange}
                        min="1"
                        max="50"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="total-seats-display">
                  <span>Total Seats:</span>
                  <strong style={{ color: '#4CAF50' }}>{totalSeats}</strong>
                </div>
              </div>
            )}

            {/* Standing Capacity */}
            {ticketType === 'standing' && (
              <div className="property-section standing-section">
                <h4>
                  <FontAwesomeIcon icon={faUsers} style={{ marginRight: '8px' }} />
                  Standing
                </h4>
                
                <div className="property-row">
                  <label>Capacity</label>
                  <input 
                    type="number" 
                    value={standingCapacity}
                    onChange={(e) => setStandingCapacity(parseInt(e.target.value) || 0)}
                    min="0"
                    placeholder="0 = Unlimited"
                  />
                  <small style={{ fontSize: '10px', color: '#bdc3c7', marginTop: '4px', display: 'block' }}>
                    0 = unlimited capacity
                  </small>
                </div>
              </div>
            )}

            {/* No Tickets Info */}
            {ticketType === 'none' && (
              <div className="property-section no-tickets-section">
                <div style={{
                  background: '#3a1a1a',
                  padding: '12px',
                  borderRadius: '8px',
                  textAlign: 'center',
                  border: '1px solid #dc3545'
                }}>
                  <div style={{ fontSize: '28px' }}>🚫</div>
                  <p style={{ margin: '8px 0 0 0', fontSize: '11px' }}>No ticket sales for this event</p>
                </div>
              </div>
            )}

            <div className="property-divider"></div>

            <div className="property-section map-elements-section">
              <h4>
                <FontAwesomeIcon icon={faMapMarkerAlt} style={{ marginRight: '8px' }} />
                Map Elements
              </h4>
              
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input type="checkbox" checked={showEntrance} onChange={(e) => setShowEntrance(e.target.checked)} />
                  <span>🚪 Entrance</span>
                </label>
                <label className="checkbox-label">
                  <input type="checkbox" checked={showExit} onChange={(e) => setShowExit(e.target.checked)} />
                  <span>🚪 Exit</span>
                </label>
                <label className="checkbox-label">
                  <input type="checkbox" checked={showStage} onChange={(e) => setShowStage(e.target.checked)} />
                  <span>🎪 Stage</span>
                </label>
                <label className="checkbox-label">
                  <input type="checkbox" checked={showParking} onChange={(e) => setShowParking(e.target.checked)} />
                  <span>🅿️ Parking</span>
                </label>
                <label className="checkbox-label">
                  <input type="checkbox" checked={showFoodCourt} onChange={(e) => setShowFoodCourt(e.target.checked)} />
                  <span>🍔 Food Court</span>
                </label>
                <label className="checkbox-label">
                  <input type="checkbox" checked={showRestrooms} onChange={(e) => setShowRestrooms(e.target.checked)} />
                  <span>🚻 Restrooms</span>
                </label>
                <label className="checkbox-label">
                  <input type="checkbox" checked={showFirstAid} onChange={(e) => setShowFirstAid(e.target.checked)} />
                  <span>🏥 First Aid</span>
                </label>
              </div>

              {showParking && (
                <div className="parking-config" style={{ marginTop: '10px' }}>
                  <div className="property-row">
                    <label>Parking Zones</label>
                    <select 
                      value={parkingZones}
                      onChange={(e) => setParkingZones(parseInt(e.target.value))}
                    >
                      <option value="1">1 Zone</option>
                      <option value="2">2 Zones</option>
                      <option value="3">3 Zones</option>
                    </select>
                  </div>
                  <label className="checkbox-label">
                    <input type="checkbox" checked={hasVIPParking} onChange={(e) => setHasVIPParking(e.target.checked)} />
                    <span>✨ VIP Parking</span>
                  </label>
                </div>
              )}
            </div>

            {selectedId ? (
              <div className="property-editor">
                {stalls.find(s => s.id === selectedId) ? (
                  <>
                    <h4>Stall #{stalls.find(s => s.id === selectedId)?.number}</h4>
                    
                    <div className="property-row">
                      <label>Category</label>
                      <select 
                        value={stalls.find(s => s.id === selectedId)?.category}
                        onChange={(e) => {
                          const cat = stallCategories.find(c => c.id === e.target.value);
                          const updated = stalls.map(s => 
                            s.id === selectedId ? { 
                              ...s, 
                              category: e.target.value,
                              color: cat.color,
                              icon: cat.icon,
                              textColor: cat.textColor
                            } : s
                          );
                          addToHistory(updated, fixedElements);
                          setStalls(updated);
                        }}
                      >
                        {stallCategories.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.label}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="property-row">
                      <label>Price (₹)</label>
                      <input 
                        type="number" 
                        value={stalls.find(s => s.id === selectedId)?.price}
                        onChange={(e) => {
                          const updated = stalls.map(s => 
                            s.id === selectedId ? { ...s, price: Number(e.target.value) } : s
                          );
                          addToHistory(updated, fixedElements);
                          setStalls(updated);
                        }}
                      />
                    </div>
                    
                    <div className="property-row description-field">
                      <label>Description</label>
                      <textarea
                        value={stalls.find(s => s.id === selectedId)?.description || ''}
                        onChange={(e) => {
                          const updated = stalls.map(s => 
                            s.id === selectedId ? { ...s, description: e.target.value } : s
                          );
                          addToHistory(updated, fixedElements);
                          setStalls(updated);
                        }}
                        placeholder="Stall description..."
                        rows="3"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <h4>Fixed Element</h4>
                    <div className="property-row">
                      <label>Position</label>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <input 
                          type="number" 
                          value={Math.round(fixedElements.find(e => e.id === selectedId)?.x)}
                          onChange={(e) => {
                            const updated = fixedElements.map(el => 
                              el.id === selectedId ? { ...el, x: Number(e.target.value) } : el
                            );
                            addToHistory(stalls, updated);
                            setFixedElements(updated);
                          }}
                          placeholder="X"
                        />
                        <input 
                          type="number" 
                          value={Math.round(fixedElements.find(e => e.id === selectedId)?.y)}
                          onChange={(e) => {
                            const updated = fixedElements.map(el => 
                              el.id === selectedId ? { ...el, y: Number(e.target.value) } : el
                            );
                            addToHistory(stalls, updated);
                            setFixedElements(updated);
                          }}
                          placeholder="Y"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="no-selection">
                <p>Click a stall or element</p>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default AdminLayoutBuilder;